/**
 * Provider registry — singleton managing all AI provider adapters.
 * Registers providers on init, tracks availability status with TTL.
 */

import type { AIProviderAdapter } from './providers/base-provider';
import { OllamaProvider } from './providers/ollama-provider';
import { AnthropicProvider } from './providers/anthropic-provider';
import { OpenRouterProvider } from './providers/openrouter-provider';
import { BrowserProvider } from './providers/browser-provider';
import { DemoProvider } from './providers/demo-provider';
import { eventBus } from '@/core/events';

/** When true, the demo provider is active (no API tokens consumed). */
let demoMode = false;

/** Check if demo mode is currently active. */
export function isDemoMode(): boolean {
  return demoMode;
}

/** Enable or disable demo mode at runtime. */
export function setDemoMode(enabled: boolean): void {
  demoMode = enabled;
  if (enabled) {
    // Ensure demo provider is registered and marked available
    if (!providers.has('demo')) {
      registerProvider(new DemoProvider());
    }
    availabilityCache.set('demo', { available: true, cachedAt: Date.now() });
    eventBus.emit('ai:provider:status', { providerId: 'demo', available: true });
  } else {
    availabilityCache.delete('demo');
    providers.delete('demo');
    eventBus.emit('ai:provider:status', { providerId: 'demo', available: false });
  }
}

const providers = new Map<string, AIProviderAdapter>();

/** TTL-aware availability cache entry. */
interface CacheEntry {
  available: boolean;
  cachedAt: number;
}

/** Cache false results for 30s so unavailable providers are re-checked quickly. */
const UNAVAILABLE_TTL_MS = 30_000;
/** Cache true results for 60s to avoid redundant health checks. */
const AVAILABLE_TTL_MS = 60_000;

const availabilityCache = new Map<string, CacheEntry>();

/** Check if a cache entry is still valid. */
function isCacheValid(entry: CacheEntry): boolean {
  const ttl = entry.available ? AVAILABLE_TTL_MS : UNAVAILABLE_TTL_MS;
  return Date.now() - entry.cachedAt < ttl;
}

/** Register a provider adapter. */
export function registerProvider(provider: AIProviderAdapter): void {
  providers.set(provider.id, provider);
}

/** Get a provider by ID. */
export function getProvider(id: string): AIProviderAdapter | undefined {
  return providers.get(id);
}

/** Get all registered providers. */
export function getAllProviders(): AIProviderAdapter[] {
  return Array.from(providers.values());
}

/** Get IDs of providers currently known to be available. */
export function getAvailableProviderIds(): string[] {
  return Array.from(availabilityCache.entries())
    .filter(([, entry]) => entry.available && isCacheValid(entry))
    .map(([id]) => id);
}

/** Check availability of all providers and emit status events. */
export async function discoverAvailable(): Promise<Map<string, boolean>> {
  const results = new Map<string, boolean>();
  const now = Date.now();

  const checks = Array.from(providers.entries()).map(async ([id, provider]) => {
    try {
      const available = await provider.isAvailable();
      results.set(id, available);
      availabilityCache.set(id, { available, cachedAt: now });
      eventBus.emit('ai:provider:status', { providerId: id, available });
    } catch {
      results.set(id, false);
      availabilityCache.set(id, { available: false, cachedAt: now });
      eventBus.emit('ai:provider:status', { providerId: id, available: false });
    }
  });

  await Promise.allSettled(checks);
  return results;
}

/** Check if a specific provider is available (uses TTL cache, refreshes when stale). */
export async function isProviderAvailable(id: string): Promise<boolean> {
  const cached = availabilityCache.get(id);
  if (cached !== undefined && isCacheValid(cached)) return cached.available;

  const provider = providers.get(id);
  if (!provider) return false;

  const now = Date.now();
  try {
    const available = await provider.isAvailable();
    availabilityCache.set(id, { available, cachedAt: now });
    return available;
  } catch {
    availabilityCache.set(id, { available: false, cachedAt: now });
    return false;
  }
}

/**
 * Report a provider failure — used by fallback chain to mark providers
 * unavailable after consecutive failures without waiting for TTL expiry.
 */
export function reportProviderFailure(id: string): void {
  availabilityCache.set(id, { available: false, cachedAt: Date.now() });
  eventBus.emit('ai:provider:status', { providerId: id, available: false });
}

/** Clear availability cache (forces re-discovery on next check). */
export function clearAvailabilityCache(): void {
  availabilityCache.clear();
}

/** Initialize all built-in providers and run initial discovery. */
export async function initProviders(): Promise<void> {
  // Only register Ollama if explicitly configured — avoids polling localhost:11434.
  // Optional chaining: import.meta.env only exists under Vite, not in Node runs.
  if (import.meta.env?.VITE_OLLAMA_ENDPOINT) {
    registerProvider(new OllamaProvider());
  }
  registerProvider(new AnthropicProvider());
  registerProvider(new OpenRouterProvider());
  registerProvider(new BrowserProvider());
  registerProvider(new DemoProvider());

  await discoverAvailable();

  // Auto-enable demo mode if no real providers are available
  const realProviders = ['ollama', 'anthropic', 'openrouter'];
  const anyRealAvailable = realProviders.some(id => {
    const entry = availabilityCache.get(id);
    return entry?.available === true;
  });

  if (!anyRealAvailable) {
    demoMode = true;
    console.info('[Sentinel] No AI providers available — demo mode enabled automatically.');
  }
}
